import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Form, Input, Button, Alert, Row, Col, Select } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  MedicineBoxOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { toast } from '../../../utils/toast.js';
import { PROVINCES, getWardsByProvinceCode } from '../../../constants/vietnamAdministrativeDivisions.js';
import { PHARMACY_SCALES } from '../../../constants/userProfileOptions.js';

const PROVINCE_OPTIONS = PROVINCES.map((p) => ({
  label: p.fullName,
  value: p.name,
  code: p.code,
}));

const LEAD_SOURCES = [
  { label: 'Kênh facebook, tiktok Thế Thảo pharma', value: 'Kênh Facebook, tiktok Thế Thảo pharma' },
  { label: 'Facebook, page daotaonhathuoc.com', value: 'Facebook, page daotaonhathuoc.com' },
  { label: 'Bạn bè giới thiệu', value: 'Bạn bè giới thiệu' },
  { label: 'Sale Thế Thảo chia sẻ', value: 'Sale Thế Thảo chia sẻ' },
  { label: 'Facebook, tiktok của founder Thế Thảo pharma (chị Thảo, anh Thế)', value: 'Facebook, tiktok của founder Thế Thảo pharma (chị Thảo, anh Thế)' },
  { label: 'Khác', value: 'Khác' },
];

export function RegisterPage() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [wardOptions, setWardOptions] = useState([]);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleProvinceChange = (value, option) => {
    form.setFieldsValue({ district: undefined });
    const wards = getWardsByProvinceCode(option?.code);
    setWardOptions(wards.map((w) => ({ label: w.fullName, value: w.fullName })));
  };

  const onFinish = async (values) => {
    setLoading(true);
    setErrorMsg('');
    try {
      await register(values);
      toast.success('Tạo tài khoản thành công! Chào mừng bạn đến với PharmaLMS.');
      navigate('/courses');
    } catch (err) {
      const msg = err.message || 'Đăng ký không thành công. Vui lòng kiểm tra lại thông tin.';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: 'calc(100vh - 140px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 16px',
      }}
    >
      <Card
        style={{ width: '100%', maxWidth: 600, borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
        styles={{ body: { padding: '32px 28px' } }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 10,
              background: '#005537',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              marginBottom: 12,
            }}
          >
            <MedicineBoxOutlined />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
            Đăng Ký Tài Khoản
          </h2>
          <p style={{ color: '#64748b', fontSize: 13 }}>
            Nhận ngay quyền truy cập <strong>7 Module Miễn Phí</strong> cùng toàn bộ tài liệu công cụ thực chiến
          </p>
        </div>

        {errorMsg && (
          <Alert message={errorMsg} type="error" showIcon style={{ marginBottom: 16, fontSize: 13 }} />
        )}

        <Form form={form} layout="vertical" onFinish={onFinish} size="middle">
          <Form.Item
            label="Họ và tên Dược sĩ / Học viên"
            name="fullName"
            rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
          >
            <Input prefix={<UserOutlined style={{ color: '#94a3b8' }} />} placeholder="Dược sĩ Nguyễn Thị Mai" />
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Email đăng nhập"
                name="email"
                rules={[
                  { required: true, message: 'Vui lòng nhập email' },
                  { type: 'email', message: 'Email không hợp lệ' },
                ]}
              >
                <Input prefix={<MailOutlined style={{ color: '#94a3b8' }} />} placeholder="duocsi@gmail.com" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Số điện thoại / Zalo"
                name="phone"
                rules={[{ required: true, message: 'Vui lòng nhập SĐT để kích hoạt tài khoản' }]}
              >
                <Input prefix={<PhoneOutlined style={{ color: '#94a3b8' }} />} placeholder="0912345678" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Tỉnh / Thành phố"
                name="province"
                rules={[{ required: true, message: 'Vui lòng chọn Tỉnh/Thành' }]}
              >
                <Select
                  placeholder="Chọn Tỉnh / Thành"
                  showSearch
                  optionFilterProp="label"
                  prefix={<EnvironmentOutlined style={{ color: '#94a3b8' }} />}
                  options={PROVINCE_OPTIONS}
                  onChange={handleProvinceChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Phường / Xã"
                name="district"
                rules={[{ required: true, message: 'Vui lòng chọn Phường/Xã' }]}
              >
                <Select
                  placeholder="Chọn Tỉnh/Thành trước"
                  showSearch
                  optionFilterProp="label"
                  disabled={wardOptions.length === 0}
                  notFoundContent="Vui lòng chọn Tỉnh/Thành trước"
                  prefix={<EnvironmentOutlined style={{ color: '#94a3b8' }} />}
                  options={wardOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Số Zalo liên hệ"
                name="zaloNumber"
                rules={[{ required: true, message: 'Vui lòng nhập số Zalo để hỗ trợ đào tạo' }]}
              >
                <Input prefix={<PhoneOutlined style={{ color: '#94a3b8' }} />} placeholder="0912345678" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Quy mô cơ sở hiện tại"
                name="pharmacyScale"
                initialValue="Nhà thuốc 2-4 nhân sự"
              >
                <Select
                  placeholder="Chọn quy mô nhà thuốc"
                  prefix={<TeamOutlined style={{ color: '#94a3b8' }} />}
                  options={PHARMACY_SCALES}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item label="Tên Nhà Thuốc đang công tác" name="pharmacyName">
                <Input prefix={<ShopOutlined style={{ color: '#94a3b8' }} />} placeholder="Nhà thuốc An Khang Pharma" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Bạn biết đến khóa học qua đâu?" name="leadSource" initialValue={LEAD_SOURCES[0].value}>
                <Select options={LEAD_SOURCES} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Mật khẩu"
            name="password"
            rules={[
              { required: true, message: 'Vui lòng nhập mật khẩu' },
              { min: 6, message: 'Mật khẩu tối thiểu 6 ký tự' },
            ]}
          >
            <Input.Password prefix={<LockOutlined style={{ color: '#94a3b8' }} />} placeholder="Tối thiểu 6 ký tự" />
          </Form.Item>

          <Button type="primary" htmlType="submit" block loading={loading} style={{ height: 44, marginTop: 8, fontSize: 15, fontWeight: 600, background: '#005537' }}>
            Tạo Tài Khoản & Bắt Đầu Học Miễn Phí
          </Button>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#64748b' }}>
          Đã có tài khoản?{' '}
          <Link to="/login" style={{ color: '#005537', fontWeight: 600 }}>
            Đăng nhập ngay
          </Link>
        </div>
      </Card>
    </div>
  );
}
